import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Modal from '../Modal';
import { useTheme } from '../../context/ThemeContext';
import AppIconButton from '../ui/AppIconButton';
import { todayLocalIso } from '../../utils/dateLocal';
import { PrimaryButton, SecondaryButton } from '../ui';
import { financeApi } from '../../api/financeApi';
import { formatCurrency } from '../../config/currency';
import { useCountryConfig } from '../../hooks/useCountryConfig';
import { isSingleTax, singleTaxLabel } from '../../config/tax';
import type { OcrResultData } from './InvoiceScanner';
import PaymentModeSelect from './PaymentModeSelect';

// --- Types ---

interface LineItem {
  id: string; // client-side key
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface ExpenseCategory {
  id: number;
  name: string;
}

interface OcrReviewFormProps {
  open: boolean;
  onClose: () => void;
  ocrData: OcrResultData | null;
  categories: ExpenseCategory[];
  onSave: () => void; // called after successful save to refresh parent
}

// --- Constants ---

const PAYMENT_STATUSES = [
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
];

const LOW_CONFIDENCE_THRESHOLD = 70;

// --- Component ---

const OcrReviewForm: React.FC<OcrReviewFormProps> = ({
  open,
  onClose,
  ocrData,
  categories,
  onSave,
}) => {
  const { theme } = useTheme();
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  // Form state — initialized from ocrData when it changes
  const [supplierName, setSupplierName] = useState('');
  const [date, setDate] = useState('');
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const { taxSublabels, taxLabel } = useCountryConfig();
  const singleTax = isSingleTax(taxSublabels);
  const [cgst, setCgst] = useState<number>(0);
  const [sgst, setSgst] = useState<number>(0);
  const [igst, setIgst] = useState<number>(0);
  const [vatAmount, setVatAmount] = useState<number>(0);
  const [paymentStatus, setPaymentStatus] = useState<string>('UNPAID');
  const [paymentMode, setPaymentMode] = useState<string>('CREDIT');
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Populate form when ocrData changes and modal opens
  React.useEffect(() => {
    if (open && ocrData && !initialized) {
      setSupplierName(ocrData.supplierName || '');
      setDate(ocrData.date || todayLocalIso());
      setTotalAmount(ocrData.totalAmount || 0);
      setCgst(ocrData.cgst || 0);
      setSgst(ocrData.sgst || 0);
      setIgst(ocrData.igst || 0);
      // Single-tax countries (e.g. UK VAT): collapse any parsed tax buckets into one VAT figure.
      setVatAmount((ocrData.cgst || 0) + (ocrData.sgst || 0) + (ocrData.igst || 0));
      setPaymentStatus('UNPAID');
      setPaymentMode('CREDIT');
      setDescription('');
      setCategoryId('');

      // Map line items
      const items: LineItem[] = (ocrData.lineItems || []).map((li, idx) => ({
        id: `ocr-${idx}`,
        description: li.description || '',
        quantity: li.quantity || 1,
        rate: li.rate || 0,
        amount: li.amount || 0,
      }));
      setLineItems(items);
      setInitialized(true);
    }
  }, [open, ocrData, initialized]);

  // Reset initialized flag when modal closes
  React.useEffect(() => {
    if (!open) {
      setInitialized(false);
    }
  }, [open]);

  // Computed total from line items
  const computedTotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  }, [lineItems]);

  // Confidence level
  const confidence = ocrData?.confidence || 0;
  const isLowConfidence = confidence < LOW_CONFIDENCE_THRESHOLD;

  // --- Line item handlers ---

  const handleLineItemChange = useCallback(
    (id: string, field: keyof LineItem, value: string | number) => {
      setLineItems((prev) =>
        prev.map((item) => {
          if (item.id !== id) return item;
          const updated = { ...item, [field]: value };

          // Auto-calculate amount when qty or rate changes
          if (field === 'quantity' || field === 'rate') {
            updated.amount = Number(updated.quantity || 0) * Number(updated.rate || 0);
          }
          return updated;
        })
      );
    },
    []
  );

  const handleAddLineItem = useCallback(() => {
    setLineItems((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0,
      },
    ]);
  }, []);

  const handleRemoveLineItem = useCallback((id: string) => {
    setLineItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // --- Save handler ---

  const handleSave = async () => {
    // Validation
    if (!totalAmount && !computedTotal) {
      toast.error('Please enter a total amount');
      return;
    }
    if (!date) {
      toast.error('Please enter a date');
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        categoryId: categoryId || null,
        amount: totalAmount || computedTotal,
        date,
        paymentStatus,
        paymentMode,
        supplierName: supplierName || null,
        description: description || (lineItems.length > 0
          ? `Invoice: ${lineItems.map((li) => li.description).filter(Boolean).join(', ')}`
          : null),
        dueDate: null,
        staffMemberId: null,
        // Extended fields for OCR-sourced expenses
        lineItems: lineItems.length > 0 ? lineItems.map((li) => ({
          description: li.description,
          quantity: li.quantity,
          rate: li.rate,
          amount: li.amount,
        })) : null,
        taxBreakdown: (!singleTax && (cgst || sgst || igst)) ? { cgst, sgst, igst } : null,
        vatAmount: singleTax ? (vatAmount || null) : null,
      };

      await financeApi.createExpense(payload);
      toast.success('Expense saved from scanned invoice');
      onSave();
      onClose();
    } catch (err: unknown) {
      console.error('Error saving expense from OCR:', err);
      toast.error('Failed to save expense. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // --- Shared styles ---

  const lowConfidenceBorder = {
    '& fieldset': { borderColor: `${theme.warning || '#d97706'} !important` },
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '10px',
      '& fieldset': { borderColor: theme.components?.input?.border || '#DDD8D2' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: theme.components?.input?.focus || theme.primary },
    },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.primary },
    '& .MuiInputBase-input': { color: theme.text },
  };

  const selectSx = {
    borderRadius: '10px',
    backgroundColor: theme.components?.input?.background || '#FFFFFF',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.border || '#DDD8D2' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.focus || theme.primary },
    '& .MuiSelect-select': { color: theme.text },
  };

  if (!ocrData) return null;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Review Scanned Invoice"
      maxWidth="md"
    >
      <Box sx={{ pb: 1 }}>
        {/* Top info bar: image thumbnail + parser badge */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: 2,
            borderRadius: '12px',
            backgroundColor: theme.components?.card?.background || '#FFFFFF',
            border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
          }}
        >
          {ocrData.imageDataUrl && (
            <Box
              component="img"
              src={ocrData.imageDataUrl}
              alt="Scanned invoice"
              sx={{
                width: 60,
                height: 60,
                objectFit: 'cover',
                borderRadius: '8px',
                border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                flexShrink: 0,
              }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                label={`Parsed by: ${ocrData.parser === 'gemini' ? 'Gemini Flash' : 'Java Parser'}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  backgroundColor: ocrData.parser === 'gemini' ? '#4285f420' : theme.button.primary.bg,
                  color: ocrData.parser === 'gemini' ? '#4285f4' : theme.button.primary.text,
                  border: `1px solid ${ocrData.parser === 'gemini' ? '#4285f444' : theme.button.primary.hover}`,
                }}
              />
              <Chip
                label={`${Math.round(confidence)}% confidence`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  backgroundColor: isLowConfidence
                    ? `${theme.warning || '#d97706'}20`
                    : `${theme.success || '#10b981'}20`,
                  color: isLowConfidence
                    ? theme.warning || '#d97706'
                    : theme.success || '#10b981',
                  border: `1px solid ${isLowConfidence
                    ? `${theme.warning || '#d97706'}44`
                    : `${theme.success || '#10b981'}44`}`,
                }}
              />
            </Box>
            {isLowConfidence && (
              <Typography variant="caption" sx={{ color: theme.warning || '#d97706', mt: 0.5, display: 'block' }}>
                Low confidence -- please verify all fields carefully
              </Typography>
            )}
          </Box>
        </Box>

        {/* Main form fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Row 1: Supplier + Date */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label="Supplier Name"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              fullWidth
              size="small"
              sx={{
                ...inputSx,
                ...(isLowConfidence && !supplierName ? { '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], ...lowConfidenceBorder } } : {}),
              }}
            />
            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Box>

          {/* Row 2: Total Amount + Category */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label="Total Amount"
              type="number"
              value={totalAmount || ''}
              onChange={(e) => setTotalAmount(parseFloat(e.target.value) || 0)}
              fullWidth
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
              sx={{
                ...inputSx,
                ...(isLowConfidence ? { '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], ...lowConfidenceBorder } } : {}),
              }}
            />
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: theme.gray?.[500] || '#666' }}>Category</InputLabel>
              <Select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value as number)}
                label="Category"
                sx={selectSx}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Tax breakdown — single VAT field for UK-style countries, 3-part GST otherwise */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.text, mt: 0.5 }}>
            {singleTax ? singleTaxLabel(taxSublabels) : `Tax Breakdown (${taxLabel})`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            {singleTax ? (
              <TextField
                label={singleTaxLabel(taxSublabels)}
                type="number"
                value={vatAmount || ''}
                onChange={(e) => setVatAmount(parseFloat(e.target.value) || 0)}
                fullWidth
                size="small"
                inputProps={{ min: 0, step: 0.01 }}
                sx={inputSx}
              />
            ) : (
              <>
                <TextField
                  label={taxSublabels[0] || 'CGST'}
                  type="number"
                  value={cgst || ''}
                  onChange={(e) => setCgst(parseFloat(e.target.value) || 0)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={inputSx}
                />
                <TextField
                  label={taxSublabels[1] || 'SGST'}
                  type="number"
                  value={sgst || ''}
                  onChange={(e) => setSgst(parseFloat(e.target.value) || 0)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={inputSx}
                />
                <TextField
                  label={taxSublabels[2] || 'IGST'}
                  type="number"
                  value={igst || ''}
                  onChange={(e) => setIgst(parseFloat(e.target.value) || 0)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={inputSx}
                />
              </>
            )}
          </Box>

          {/* Line items */}
          <Box sx={{ mt: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.text }}>
                Line Items
              </Typography>
              <SecondaryButton
                size="small"
                startIcon={<PlusIcon style={{ width: 16, height: 16 }} />}
                onClick={handleAddLineItem}
                sx={{ fontSize: '0.813rem' }}
              >
                Add Row
              </SecondaryButton>
            </Box>

            {lineItems.length > 0 ? (
              isMobile ? (
                // Mobile: card layout for line items
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {lineItems.map((item, idx) => (
                    <Paper
                      key={item.id}
                      sx={{
                        p: 2,
                        borderRadius: '10px',
                        backgroundColor: theme.components?.card?.background || '#FFFFFF',
                        border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Typography variant="caption" sx={{ color: theme.gray?.[500], fontWeight: 600 }}>
                          Item {idx + 1}
                        </Typography>
                        <AppIconButton
                          icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                          tooltip={`Delete item ${idx + 1}`}
                          variant="ghost"
                          size="small"
                          onClick={() => handleRemoveLineItem(item.id)}
                          sx={{ p: 0.5, '&:hover': { backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' } }}
                          data-testid={`delete-line-item-${item.id}`}
                        />
                      </Box>
                      <TextField
                        label="Description"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ ...inputSx, mb: 1.5 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                          label="Qty"
                          type="number"
                          value={item.quantity || ''}
                          onChange={(e) => handleLineItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                          size="small"
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ ...inputSx, flex: 1 }}
                        />
                        <TextField
                          label="Rate"
                          type="number"
                          value={item.rate || ''}
                          onChange={(e) => handleLineItemChange(item.id, 'rate', parseFloat(e.target.value) || 0)}
                          size="small"
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ ...inputSx, flex: 1 }}
                        />
                        <TextField
                          label="Amount"
                          type="number"
                          value={item.amount || ''}
                          onChange={(e) => handleLineItemChange(item.id, 'amount', parseFloat(e.target.value) || 0)}
                          size="small"
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ ...inputSx, flex: 1 }}
                        />
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                // Desktop: table layout for line items
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: '10px',
                    border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                    backgroundColor: theme.components?.card?.background || '#FFFFFF',
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: theme.text, fontSize: '0.813rem' }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.text, fontSize: '0.813rem', width: 80 }} align="right">Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.text, fontSize: '0.813rem', width: 100 }} align="right">Rate</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.text, fontSize: '0.813rem', width: 110 }} align="right">Amount</TableCell>
                        <TableCell sx={{ width: 40 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <TextField
                              value={item.description}
                              onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                              size="small"
                              fullWidth
                              variant="standard"
                              sx={{ '& .MuiInputBase-input': { color: theme.text, fontSize: '0.875rem' } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              value={item.quantity || ''}
                              onChange={(e) => handleLineItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                              size="small"
                              type="number"
                              variant="standard"
                              inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                              sx={{ '& .MuiInputBase-input': { color: theme.text, fontSize: '0.875rem' } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              value={item.rate || ''}
                              onChange={(e) => handleLineItemChange(item.id, 'rate', parseFloat(e.target.value) || 0)}
                              size="small"
                              type="number"
                              variant="standard"
                              inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                              sx={{ '& .MuiInputBase-input': { color: theme.text, fontSize: '0.875rem' } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              value={item.amount || ''}
                              onChange={(e) => handleLineItemChange(item.id, 'amount', parseFloat(e.target.value) || 0)}
                              size="small"
                              type="number"
                              variant="standard"
                              inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                              sx={{ '& .MuiInputBase-input': { color: theme.text, fontSize: '0.875rem' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <AppIconButton
                              icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                              tooltip="Delete item"
                              variant="ghost"
                              size="small"
                              onClick={() => handleRemoveLineItem(item.id)}
                              sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' } }}
                              data-testid={`delete-table-line-item-${item.id}`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total row */}
                      <TableRow>
                        <TableCell colSpan={3} align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>
                            Line Items Total
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: theme.primary }}>
                            {formatCurrency(computedTotal)}
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            ) : (
              <Typography variant="body2" sx={{ color: theme.gray?.[400] || '#AAA', fontStyle: 'italic', py: 1 }}>
                No line items detected. Click "Add Row" to add manually.
              </Typography>
            )}

            {/* Mobile: show computed total */}
            {isMobile && lineItems.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: '8px',
                  backgroundColor: theme.button.secondary.bg,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                  Line Items Total
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: theme.primary }}>
                  {formatCurrency(computedTotal)}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 0.5 }} />

          {/* Payment fields */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: theme.gray?.[500] || '#666' }}>Payment Status</InputLabel>
              <Select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                label="Payment Status"
                sx={selectSx}
              >
                {PAYMENT_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <PaymentModeSelect
              value={paymentMode}
              onChange={(v) => setPaymentMode(v)}
              label="Payment Mode"
              fullWidth
              size="small"
            />
          </Box>

          {/* Description */}
          <TextField
            label="Notes / Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Any additional notes about this expense..."
            sx={inputSx}
          />
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: `1px solid ${theme.components?.card?.border || '#E0E0E0'}` }}>
          <SecondaryButton onClick={onClose} disabled={saving} sx={{ borderRadius: '10px' }}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={saving || (!totalAmount && !computedTotal)} loading={saving} sx={{ borderRadius: '10px', px: 3 }}>Save as Expense</PrimaryButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default OcrReviewForm;
